import React, { useCallback, useEffect, useMemo } from 'react';
import { Redirect, Route, useLocation } from 'react-router-dom';
import CacheRoute, {
  CacheSwitch,
  CacheRouteProps,
  clearCache,
  dropByCacheKey,
} from 'react-router-cache-route';

import Loadable from 'react-loadable';

import { DesktopFrame, Loading, NeedLogin } from '@/components';
import { getEnableEditorDebugger, tryConnectToWallet, history } from '@/utils';

import { useCurrentAddressInfo, useWindowVisible } from './hooks';
import { DesktopContainer } from './views/Profile/Container';
import Welcome from './views/Welcome';
import Account from './views/Account';
import Pro from './views/Pro';
import { useInfiniteHiSessionList } from './views/Chats/hooks/hiSession';
import { OfficialAccountRedirect } from './views/OfficialAccount/OfficialAccountRedirect';

const enableEditorDebugger = getEnableEditorDebugger();

const HiWhitepaper = Loadable({
  loader: () => import('./views/Hi/Whitepaper'),
  loading: () => <Loading />,
  delay: 500,
});

const HiThirdParty = Loadable({
  loader: () => import('./views/Hi/ThirdParty'),
  loading: () => <Loading />,
  delay: 500,
});

const Connect = Loadable({
  loader: () => import('./views/Connect'),
  loading: () => <Loading />,
  delay: 500,
});

const Proposal = Loadable({
  loader: () => import('./views/Proposal'),
  loading: () => <Loading />,
  delay: 500,
});

const NotFound = Loadable({
  loader: () => import('./components/NotFound'),
  loading: () => <Loading />,
  delay: 500,
});
const Profile = Loadable({
  loader: () => import('./views/Profile'),
  loading: () => <Loading />,
  delay: 500,
});
const OfficialAccount = Loadable({
  loader: () => import('./views/OfficialAccount'),
  loading: () => <Loading />,
  delay: 500,
});
const Group = Loadable({
  loader: () => import('./views/Group'),
  loading: () => <Loading />,
  delay: 500,
});
const CreateGroup = Loadable({
  loader: () => import('./views/Group/components/CreateGroup'),
  loading: () => <Loading />,
  delay: 500,
});
const CreateGroupProposal = Loadable({
  loader: () => import('./views/GroupProposal/components/CreateGroupProposal'),
  loading: () => <Loading />,
  delay: 500,
});
const SetGroupEligibilityProposal = Loadable({
  loader: () => import('./views/GroupProposal/components/SetGroupEligibilityProposal'),
  loading: () => <Loading />,
  delay: 500,
});
const Bundles = Loadable({
  loader: () => import('./views/Bundles'),
  loading: () => <Loading />,
  delay: 500,
});
const Settings = Loadable({
  loader: () => import('./views/Settings'),
  loading: () => <Loading />,
  delay: 500,
});
const Ranking = Loadable({
  loader: () => import('./views/Ranking'),
  loading: () => <Loading />,
  delay: 500,
});
const Groups = Loadable({
  loader: () => import('./views/Group/components/Groups'),
  loading: () => <Loading />,
  delay: 500,
});

// const OfficialAccountList = Loadable({
//   loader: () => import('./views/OfficialAccountList'),
//   loading: () => <Loading />,
//   delay: 500,
// });

// const NFTs = Loadable({
//   loader: () => import('./views/NFTs'),
//   loading: () => <Loading />,
//   delay: 500,
// });

const Download = Loadable({
  loader: () => import('./views/Download'),
  loading: () => <Loading />,
  delay: 500,
});

const Chats = Loadable({
  loader: () => import('./views/Chats'),
  loading: () => <Loading />,
  delay: 500,
});

// const Activity = Loadable({
//   loader: () => import('./views/Activity'),
//   loading: () => <Loading />,
//   delay: 500,
// });

const DebuggerEditor = Loadable({
  loader: () => import('./components/RichTextEditor/debuggerEditor'),
  loading: () => <Loading />,
  delay: 500,
});

const PortfolioAnalysis = Loadable({
  loader: () => import('./views/PortfolioAnalysis'),
  loading: () => <Loading />,
  delay: 500,
});

// const Whales = Loadable({
//   loader: () => import('./views/Whales'),
//   loading: () => <Loading />,
//   delay: 500,
// });
const Protocols = Loadable({
  loader: () => import('./views/Protocols'),
  loading: () => <Loading />,
  delay: 500,
});
const Collection = Loadable({
  loader: () => import('./views/Collection'),
  loading: () => <Loading />,
  delay: 500,
});
const Token = Loadable({
  loader: () => import('./views/Token'),
  loading: () => <Loading />,
  delay: 500,
});

const Stream = Loadable({
  loader: () => import('./views/Stream'),
  loading: () => <Loading />,
  delay: 500,
});

const PostQuoteList = Loadable({
  loader: () => import('./views/PostQuoteList'),
  loading: () => <Loading />,
  delay: 500,
});

const CreateStream = Loadable({
  loader: () => import('./views/CreateStream'),
  loading: () => <Loading />,
  delay: 500,
});

const Notification = Loadable({
  loader: () => import('./views/Notification/Container'),
  loading: () => <Loading />,
  delay: 500,
});

const Bookmark = Loadable({
  loader: () => import('./views/Bookmark'),
  loading: () => <Loading />,
  delay: 500,
});

const BundleAnalysis = Loadable({
  loader: () => import('./views/BundleAnalysis'),
  loading: () => <Loading />,
  delay: 500,
});

const Badge = Loadable({
  loader: () => import('./views/Badge'),
  loading: () => <Loading />,
  delay: 500,
});

const OfficialInvitation = Loadable({
  loader: () => import('./views/OfficialInvitation'),
  loading: () => <Loading />,
  delay: 500,
});

const GroupProposal = Loadable({
  loader: () => import('./views/GroupProposal'),
  loading: () => <Loading />,
  delay: 500,
});

const Market = Loadable({
  loader: () => import('./views/Market'),
  loading: () => <Loading />,
  delay: 500,
});

const Credit = Loadable({
  loader: () => import('./views/Credit'),
  loading: () => <Loading />,
  delay: 500,
});

const SnsBind = Loadable({
  loader: () => import('./views/SnsBind'),
  loading: () => <Loading />,
  delay: 500,
});

const Quest = Loadable({
  loader: () => import('./views/Quest'),
  loading: () => <Loading />,
  delay: 500,
});

const Channels = Loadable({
  loader: () => import('./views/Channel/ListContainer'),
  loading: () => <Loading />,
  delay: 500,
});

const Channel = Loadable({
  loader: () => import('./views/Channel/DetailContainer'),
  loading: () => <Loading />,
  delay: 500,
});

const ChannelFollowing = Loadable({
  loader: () => import('./views/Channel/components/FollowerList'),
  loading: () => <Loading />,
  delay: 500,
});

const XP = Loadable({
  loader: () => import('./views/XP'),
  loading: () => <Loading />,
  delay: 500,
});

const XPClaimInvite = Loadable({
  loader: () => import('./views/XPClaimInvite'),
  loading: () => <Loading />,
  delay: 500,
});

const Sybil = Loadable({
  loader: () => import('./views/Sybil'),
  loading: () => <Loading />,
  delay: 500,
});

const RiskWarning = Loadable({
  loader: () => import('./views/RiskWarning'),
  loading: () => <Loading />,
  delay: 500,
});

const clearCacheRoute = () => {
  dropByCacheKey('cacheRouteHi');
  dropByCacheKey('cacheRouteStream');
  dropByCacheKey('cacheRouteStreamList');
  dropByCacheKey('cacheRouteBookmark');
  dropByCacheKey('cacheRouteChannel');
};

const App: React.FC = () => {
  const { address } = useCurrentAddressInfo();

  const handleCache = useCallback((props: CacheRouteProps) => {
    const { location } = props;

    // 只有下一个路由是它时，才会把当前的页面缓存
    const shouldCatch =
      ['/stream/', '/group-proposal/'].some(x => !!location?.pathname?.startsWith(x)) &&
      history.action !== 'POP';

    if (!shouldCatch) {
      clearCache();
      clearCacheRoute();
    }

    return shouldCatch;
  }, []);

  useEffect(() => {
    clearCacheRoute();
  }, [address]);

  return (
    <>
      <GlobalShare />
      <DesktopFrame>
        <CacheSwitch>
          <CacheRoute exact path={['/', '/stream']} cacheKey="cacheRouteStreamList">
            <Welcome />
          </CacheRoute>
          <Redirect exact from="/overview" to="/profile" />
          <Redirect from="/portfolio/:currentAddr" to="/profile/:currentAddr" />
          <Redirect from="/portfolio" to="/profile" />
          <Redirect from="/approve/:currentAddr" to="/profile/:currentAddr/approve" />
          <Redirect from="/history/:currentAddr" to="/profile/:currentAddr/history" />
          <Redirect from="/overview/:currentAddr" to="/profile/:currentAddr" />
          <Redirect exact from="/pro" to="/paid" />
          <Route exact path="/profile">
            <Profile />
          </Route>
          <CacheRoute exact path="/quest" when={handleCache}>
            <Quest />
          </CacheRoute>
          <CacheRoute path="/profile/:currentAddr" when={handleCache}>
            <Profile />
          </CacheRoute>
          <Route path="/official-account/:officialId">
            <OfficialAccountRedirect />
          </Route>
          <CacheRoute path="/official/:officialSlug" when={handleCache}>
            <OfficialAccount />
          </CacheRoute>
          <Route path="/group-proposal/:id">
            <NeedLogin>
              <GroupProposal />
            </NeedLogin>
          </Route>
          <CacheRoute path="/group/:groupId" when={handleCache}>
            <NeedLogin>
              <Group />
            </NeedLogin>
          </CacheRoute>
          <Route path="/channel/:id/follower" exact>
            <NeedLogin>
              <ChannelFollowing />
            </NeedLogin>
          </Route>
          <CacheRoute path="/channel/:id" cacheKey="cacheRouteChannel">
            <NeedLogin>
              <Channel />
            </NeedLogin>
          </CacheRoute>
          <Route path={['/channels/:tab', '/channels']}>
            <NeedLogin>
              <Channels />
            </NeedLogin>
          </Route>
          <Route path={['/xp/:tab', '/xp']}>
            <NeedLogin>
              <XP />
            </NeedLogin>
          </Route>
          <CacheRoute path="/bookmarks" cacheKey="cacheRouteBookmark">
            <NeedLogin>
              <Bookmark />
            </NeedLogin>
          </CacheRoute>
          <Route path={['/bundles/:bundleId', '/bundles']}>
            <NeedLogin>
              <Bundles />
            </NeedLogin>
          </Route>
          <Route path={['/create-group']}>
            <NeedLogin>
              <CreateGroup />
            </NeedLogin>
          </Route>
          <Route path={['/create-group-proposal/:groupId']}>
            <NeedLogin>
              <CreateGroupProposal />
            </NeedLogin>
          </Route>
          <Route path={['/set-group-eligibility-proposal/:groupId']}>
            <NeedLogin>
              <SetGroupEligibilityProposal />
            </NeedLogin>
          </Route>
          <Route path="/connect">
            <NeedLogin>
              <Connect />
            </NeedLogin>
          </Route>
          <Route path="/settings">
            <NeedLogin>
              <Settings />
            </NeedLogin>
          </Route>
          <Route path="/paid">
            <Pro />
          </Route>
          <Route path={'/ranking'}>
            <Ranking />
          </Route>
          {/* <Route path={'/official-accounts'}>
            <OfficialAccountList />
          </Route> */}
          <Route path={'/groups'}>
            <NeedLogin>
              <Groups />
            </NeedLogin>
          </Route>
          <Route path={'/download'}>
            <Download />
          </Route>
          <Route path={'/hi-thirdparty'}>
            <HiThirdParty />
          </Route>
          <CacheRoute path="/hi" cacheKey="cacheRouteHi">
            <NeedLogin>
              <Chats />
            </NeedLogin>
          </CacheRoute>
          <Route path={'/hi-whitepaper'}>
            <HiWhitepaper />
          </Route>
          <Route path={'/account'}>
            <NeedLogin>
              <Account />
            </NeedLogin>
          </Route>
          {enableEditorDebugger ? (
            <Route path={'/debugger-editor'}>
              <DebuggerEditor />
            </Route>
          ) : null}
          <Route path={'/analysis/:currentAddr'}>
            <NeedLogin>
              <PortfolioAnalysis />
            </NeedLogin>
          </Route>
          {/* <Route path={'/whales'}>
            <Whales />
          </Route> */}
          {/* make sure the route order is correct */}
          <Route path="/protocols/pool/:poolId/:chainId">
            <Protocols />
          </Route>
          <Route path="/protocols/:protocolId">
            <Protocols />
          </Route>
          <Route path={'/protocols'}>
            <Protocols />
          </Route>
          <Route path={['/proposal/:id', '/proposal']}>
            <Proposal />
          </Route>
          <Route path={['/token/:chain/:id']}>
            <Token />
          </Route>
          <Route path={['/nfts/:chain/:id']}>
            <Collection />
          </Route>
          {/* <Route path={['/nfts']}>
          <NFTs />
        </Route> */}
          <CacheRoute path={'/stream/:id/quotes'} when={handleCache}>
            <PostQuoteList />
          </CacheRoute>
          <CacheRoute path={'/stream/:id'}>
            <Stream />
          </CacheRoute>
          <Route path={'/new-stream'}>
            <NeedLogin>
              <CreateStream />
            </NeedLogin>
          </Route>
          <Route path={'/edit-stream'}>
            <NeedLogin>
              <CreateStream isEdit />
            </NeedLogin>
          </Route>
          <Route path={'/notification'}>
            <NeedLogin>
              <Notification />
            </NeedLogin>
          </Route>
          <Route path={'/bundle-analysis/:bundleId'}>
            <NeedLogin>
              <BundleAnalysis />
            </NeedLogin>
          </Route>
          {/* <Route path={'/activity'}>
            <Activity />
          </Route> */}
          <Route path={['/badge/:id', '/official-badge/:id', '/badge']}>
            <Badge />
          </Route>
          <Route path={'/official-invitation/:token'}>
            <NeedLogin>
              <OfficialInvitation />
            </NeedLogin>
          </Route>
          <Route path={'/market'}>
            <NeedLogin>
              <Market />
            </NeedLogin>
          </Route>
          <Route path="/credit/:mode?">
            <NeedLogin>
              <Credit />
            </NeedLogin>
          </Route>
          <Route path="/sns-bind/:client">
            <SnsBind />
          </Route>
          <Route path={'/claim'}>
            <XPClaimInvite />
          </Route>
          <Route path={'/sybil'}>
            <NeedLogin>
              <Sybil />
            </NeedLogin>
          </Route>
          <Route path={'/link'}>
            <NeedLogin>
              <RiskWarning />
            </NeedLogin>
          </Route>
          <Route component={NotFound} />
        </CacheSwitch>
      </DesktopFrame>
    </>
  );
};

const GlobalShare = () => {
  const location = useLocation();
  const isVisible = useWindowVisible();
  // preload hi session list
  const isHiDetailsPage = useMemo(() => {
    const pathname = location.pathname;
    return pathname.startsWith('/hi') && !pathname.includes('/hi-whitepaper');
  }, [location.pathname]);

  useInfiniteHiSessionList(undefined, undefined, !isVisible || isHiDetailsPage);

  useEffect(() => {
    setTimeout(() => {
      Profile.preload();
      Chats.preload();
      Stream.preload();
      NotFound.preload();
      DesktopContainer.preload();
      Bundles.preload();
      GroupProposal.preload();
    }, 3000);
  }, []);

  useEffect(() => {
    setTimeout(() => {
      tryConnectToWallet().then(e => {
        console.debug(`Connected to ${e}`);
      });
    }, 2000);
  }, []);

  return null;
};

export default App;
